import React, { useMemo, useState, useEffect } from 'react';
import Button from 'shared/components/UC/Button';
import Toast from 'shared/components/Toast';
import { Evaluator, EvaluatorAssignment, AssignEvaluatorRequest, InviteEvaluatorRequest } from '../../types/evaluator';
import { ApplicationWithAdminData } from '../../types/admin';
import adminService from '../../services/adminService';
import styles from './EvaluatorAssignmentModal.module.css';

export interface EvaluatorAssignmentModalProps {
  application: ApplicationWithAdminData;
  isOpen: boolean;
  onClose: () => void;
  onAssignmentComplete: () => void;
}

type DeclineHistoryEntry = {
  applicationNumber: string;
  kind: 'declined' | 'conflict_declared';
  reasonLabel: string;
  note: string | null;
};

const EvaluatorAssignmentModal: React.FC<EvaluatorAssignmentModalProps> = ({
  application,
  isOpen,
  onClose,
  onAssignmentComplete,
}) => {
  const [availableEvaluators, setAvailableEvaluators] = useState<Evaluator[]>([]);
  const [currentAssignments, setCurrentAssignments] = useState<EvaluatorAssignment[]>([]);
  const [evalProfiles, setEvalProfiles] = useState<Record<string, { score: number | null; latestNote: string | null }>>({});
  const [declineHistories, setDeclineHistories] = useState<Record<string, DeclineHistoryEntry[]>>({});
  const [loading, setLoading] = useState(false);
  const [assigningEvaluator, setAssigningEvaluator] = useState(false);
  const [invitingExternal, setInvitingExternal] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [evalSearchText, setEvalSearchText] = useState('');
  const [inviteEvaluatorData, setInviteEvaluatorData] = useState({
    email: '',
    name: '',
  });
  const [toast, setToast] = useState<{
    message: string;
    type: 'error' | 'success' | 'warning' | 'info';
  } | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadData();
      setSelectedIds(new Set());
      setEvalSearchText('');
    }
  }, [isOpen, application.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const alreadyAssignedIds = useMemo(
    () => new Set(currentAssignments.map((a) => a.evaluator.id)),
    [currentAssignments]
  );

  // Evaluadores activos = los que no rechazaron ni declararon conflicto
  const activeCount = useMemo(
    () =>
      currentAssignments.filter(
        (a) => a.status !== 'declined' && a.status !== 'conflict_declared'
      ).length,
    [currentAssignments]
  );

  // Límite: sin cobertura → 2, con 1 activo → 1, con ≥2 activos (árbitro) → 1
  const selectionLimit = activeCount === 0 ? 2 : 1;

  const evalCandidates = useMemo(() => {
    const candidates = availableEvaluators.filter((e) => !alreadyAssignedIds.has(e.id));
    if (!evalSearchText.trim()) return candidates;
    const q = evalSearchText.toLowerCase();
    return candidates.filter(
      (e) => e.name.toLowerCase().includes(q) || e.email.toLowerCase().includes(q)
    );
  }, [availableEvaluators, alreadyAssignedIds, evalSearchText]);

  const toggleEval = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < selectionLimit) {
        next.add(id);
      }
      return next;
    });
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [evaluators, assignments, histories] = await Promise.all([
        adminService.getAvailableEvaluators(),
        adminService.getApplicationEvaluators(application.id),
        adminService.getEvaluatorDeclineHistories(),
      ]);
      setAvailableEvaluators(evaluators);
      setCurrentAssignments(assignments);
      setDeclineHistories(histories);

      // Load admin profiles for all evaluators (available + assigned)
      const allIds = [
        ...evaluators.map((e) => e.id),
        ...assignments.map((a) => a.evaluator.id),
      ];
      const uniqueIds = Array.from(new Set(allIds));
      const profiles = await Promise.all(uniqueIds.map((id) => adminService.getEvaluatorProfile(id)));
      const profileMap: Record<string, { score: number | null; latestNote: string | null }> = {};
      uniqueIds.forEach((id, i) => {
        const noteArr: any[] = profiles[i].notes ?? [];
        profileMap[id] = {
          score: profiles[i].score,
          latestNote: noteArr.length ? noteArr[noteArr.length - 1].text : null,
        };
      });
      setEvalProfiles(profileMap);
    } catch (error) {
      setToast({
        message: error instanceof Error ? error.message : 'Error al cargar datos',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignEvaluator = async () => {
    if (selectedIds.size === 0) return;

    setAssigningEvaluator(true);
    let successCount = 0;
    let firstError: string | null = null;
    const ids = Array.from(selectedIds);
    const total = ids.length;

    for (const id of ids) {
      try {
        const request: AssignEvaluatorRequest = { evaluatorId: id };
        await adminService.assignEvaluator(application.id, request);
        successCount++;
      } catch (error) {
        if (!firstError)
          firstError = error instanceof Error ? error.message : 'Error al asignar';
      }
    }

    setAssigningEvaluator(false);

    if (successCount === total) {
      setToast({
        message: `${successCount} evaluador${successCount > 1 ? 'es asignados' : ' asignado'} exitosamente.`,
        type: 'success',
      });
    } else if (successCount > 0) {
      setToast({
        message: `Se asignó ${successCount} de ${total}. ${firstError ?? ''}`.trim(),
        type: 'warning',
      });
    } else {
      setToast({ message: firstError ?? 'Error al asignar evaluadores', type: 'error' });
    }

    setSelectedIds(new Set());
    setEvalSearchText('');
    await loadData();
    if (successCount > 0) onAssignmentComplete();
  };

  const handleInviteEvaluator = async () => {
    if (!inviteEvaluatorData.email || !inviteEvaluatorData.name) {
      setToast({
        message: 'Por favor, completa el email y nombre del evaluador',
        type: 'warning',
      });
      return;
    }

    try {
      setInvitingExternal(true);

      // Use unified invitation system with automatic application assignment
      const inviteRequest: InviteEvaluatorRequest = {
        email: inviteEvaluatorData.email,
        name: inviteEvaluatorData.name,
        applicationId: application.id, // Direct assignment during invitation
      };

      const response = await adminService.inviteEvaluator(inviteRequest);

      let successMessage = 'Evaluador invitado exitosamente';
      if (response.isNewUser) {
        successMessage = response.isExternal
          ? 'Evaluador externo creado e invitado exitosamente. Se enviará un email para configurar la contraseña.'
          : 'Usuario creado y asignado como evaluador exitosamente';
      } else if (response.roleUpdated) {
        successMessage = 'Usuario existente actualizado a evaluador y asignado exitosamente';
      } else {
        successMessage = 'Evaluador existente asignado exitosamente';
      }

      if (response.assignmentId) {
        successMessage += ' La asignación se completó automáticamente.';
      }

      setToast({
        message: successMessage,
        type: 'success',
      });

      // Reset form and reload data
      setInviteEvaluatorData({ email: '', name: '' });
      setShowInviteForm(false);
      await loadData();
      onAssignmentComplete();
    } catch (error) {
      setToast({
        message: error instanceof Error ? error.message : 'Error al invitar evaluador',
        type: 'error',
      });
    } finally {
      setInvitingExternal(false);
    }
  };

  const handleRemoveAssignment = async (evaluatorId: string) => {
    const confirmed = window.confirm('¿Está seguro de que desea remover esta asignación?');
    if (!confirmed) return;

    try {
      await adminService.removeEvaluatorAssignment(application.id, evaluatorId);

      setToast({
        message: 'Asignación removida exitosamente',
        type: 'success',
      });

      await loadData();
      onAssignmentComplete();
    } catch (error) {
      setToast({
        message: error instanceof Error ? error.message : 'Error al remover asignación',
        type: 'error',
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>Gestionar Evaluadores</h2>
          <p className={styles.applicationInfo}>
            Aplicación: {application.applicationNumber} - {application.answers?.sectionAnswers?.PROJECT_INFORMATION?.projectTitle || 'Sin título'}
          </p>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>Cargando...</div>
          ) : (
            <>
              {/* Current Assignments */}
              <div className={styles.section}>
                <h3>Evaluadores Asignados ({currentAssignments.length})</h3>
                {currentAssignments.length === 0 ? (
                  <p className={styles.emptyState}>No hay evaluadores asignados</p>
                ) : (
                  <div className={styles.assignmentsList}>
                    {currentAssignments.map((assignment, index) => (
                      <div key={assignment.id} className={styles.assignmentCard}>
                        <div className={styles.evaluatorNumber}>
                          {index + 1}
                        </div>
                        <div className={styles.evaluatorInfo}>
                          <h4>{assignment.evaluator.name}</h4>
                          <p>{assignment.evaluator.email}</p>
                          <p className={styles.assignmentDate}>
                            Asignado: {new Date(assignment.assignedAt).toLocaleDateString('es-CL', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }).replace(/\s+([ap])\.\s+m\./, ' $1.m.')}
                          </p>
                          <div className={styles.badges}>
                            <span className={`${styles.badge} ${styles[assignment.status] ?? styles.assigned}`}>
                              {assignment.status === 'pending' && 'Sin respuesta'}
                              {assignment.status === 'accepted_not_started' && 'Sin iniciar'}
                              {assignment.status === 'in_progress' && 'En evaluación'}
                              {assignment.status === 'completed' && 'Entregada'}
                              {assignment.status === 'declined' && (() => {
                                const DECLINE_LABELS: Record<string, string> = {
                                  thematic: 'Excusada — Área no afín',
                                  conflict: 'Excusada — Conflicto de interés',
                                  other: 'Excusada — Otro motivo',
                                };
                                const reason = (assignment as any).declineReason;
                                return DECLINE_LABELS[reason] ?? 'Rechazada';
                              })()}
                              {assignment.status === 'conflict_declared' && 'Conflicto declarado'}
                            </span>
                            {assignment.evaluator.isExternal && (
                              <span className={styles.externalBadge}>Externo</span>
                            )}
                            {assignment.evaluator.isExternal && !assignment.evaluator.isConfirmed && (
                              <span className={styles.pendingBadge}>Pendiente Configuración</span>
                            )}
                          </div>
                          {assignment.status === 'declined' && (assignment as any).declineNote && (
                            <p className={styles.declineNoteInline}>{(assignment as any).declineNote}</p>
                          )}
                          {(() => {
                            const p = evalProfiles[assignment.evaluator.id];
                            if (!p || (p.score === null && !p.latestNote)) return null;
                            return (
                              <div className={styles.evalProfileSummary}>
                                {p.score !== null && (
                                  <span className={styles.evalProfileScore}>
                                    <i className="uc-icon">star</i>
                                    {p.score.toFixed(1)}
                                  </span>
                                )}
                                {p.latestNote && (
                                  <span className={styles.evalProfileNote} title={p.latestNote}>
                                    {p.latestNote}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        <div className={styles.assignmentActions}>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleRemoveAssignment(assignment.evaluator.id)}
                            disabled={!!assignment.evaluation?.submittedAt}
                          >
                            Remover
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Assign Existing Evaluator */}
              <div className={styles.section}>
                <h3>Asignar Evaluador Existente</h3>
                <div className={styles.evalPanel}>
                  <div className={styles.evalFilterRow}>
                    <input
                      type="text"
                      className={styles.evalFilterInput}
                      placeholder="Filtrar por nombre o email..."
                      value={evalSearchText}
                      onChange={(e) => setEvalSearchText(e.target.value)}
                      disabled={assigningEvaluator}
                      autoComplete="off"
                    />
                    <span className={styles.evalLimitHint}>
                      {selectedIds.size === 0
                        ? `Selecciona hasta ${selectionLimit}`
                        : `${selectedIds.size} de ${selectionLimit} seleccionado${selectedIds.size > 1 ? 's' : ''}`}
                    </span>
                  </div>
                  <div className={styles.evalList}>
                    {evalCandidates.length === 0 ? (
                      <p className={styles.evalNoResults}>
                        {evalSearchText.trim()
                          ? `Sin resultados para "${evalSearchText}"`
                          : 'No hay evaluadores disponibles'}
                      </p>
                    ) : (
                      evalCandidates.map((ev) => {
                        const isChecked = selectedIds.has(ev.id);
                        const isDisabled =
                          assigningEvaluator ||
                          (!isChecked && selectedIds.size >= selectionLimit);
                        return (
                          <label
                            key={ev.id}
                            className={`${styles.evalListItem} ${isChecked ? styles.evalListItemSelected : ''} ${isDisabled ? styles.evalListItemDisabled : ''}`}
                          >
                            <input
                              type="checkbox"
                              className={styles.evalCheckbox}
                              checked={isChecked}
                              onChange={() => toggleEval(ev.id)}
                              disabled={isDisabled}
                            />
                            <span className={styles.evalName}>{ev.name}</span>
                            <span className={styles.evalEmail}>{ev.email}</span>
                            {(() => {
                              const p = evalProfiles[ev.id];
                              if (!p || (p.score === null && !p.latestNote)) return null;
                              return (
                                <span className={styles.evalProfileInline}>
                                  {p.score !== null && (
                                    <span className={styles.evalProfileScore}>
                                      <i className="uc-icon">star</i>
                                      {p.score.toFixed(1)}
                                    </span>
                                  )}
                                  {p.latestNote && (
                                    <span className={styles.evalProfileNote} title={p.latestNote}>
                                      {p.latestNote}
                                    </span>
                                  )}
                                </span>
                              );
                            })()}
                            {(() => {
                              const h = declineHistories[ev.id];
                              if (!h || h.length === 0) return null;
                              const declines = h.filter((x) => x.kind === 'declined').length;
                              const conflicts = h.filter((x) => x.kind === 'conflict_declared').length;
                              const parts: string[] = [];
                              if (declines) parts.push(`${declines} declinación${declines > 1 ? 'es' : ''}`);
                              if (conflicts) parts.push(`${conflicts} conflicto${conflicts > 1 ? 's' : ''}`);
                              const detail = h
                                .map((x) => `${x.applicationNumber}: ${x.reasonLabel}${x.note ? ` — ${x.note}` : ''}`)
                                .join('\n');
                              return (
                                <span className={styles.declineHistoryChip} title={detail}>
                                  <i className="uc-icon">history</i>
                                  {parts.join(' · ')}
                                </span>
                              );
                            })()}
                            {ev.isExternal && (
                              <span className={styles.evalTagExterno}>Externo</span>
                            )}
                          </label>
                        );
                      })
                    )}
                  </div>
                  <div className={styles.evalAssignFooter}>
                    <Button
                      variant="primary"
                      onClick={handleAssignEvaluator}
                      disabled={assigningEvaluator || selectedIds.size === 0}
                    >
                      {assigningEvaluator
                        ? 'Asignando...'
                        : selectedIds.size === 0
                        ? 'Selecciona al menos uno'
                        : `Asignar ${selectedIds.size} evaluador${selectedIds.size > 1 ? 'es' : ''}`}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Invite Evaluator */}
              <div className={styles.section}>
                <h3>Invitar Evaluador</h3>
                <p className={styles.sectionDescription}>
                  Invita evaluadores existentes o nuevos. Los usuarios existentes se actualizarán automáticamente al rol de evaluador.
                </p>
                {!showInviteForm ? (
                  <Button
                    variant="primary"
                    onClick={() => setShowInviteForm(true)}
                  >
                    Mostrar Formulario de Invitación
                  </Button>
                ) : (
                  <div className={styles.inviteForm}>
                    <div className={styles.formRow}>
                      <label className={styles.label}>
                        Email *
                        <input
                          type="email"
                          value={inviteEvaluatorData.email}
                          onChange={(e) => setInviteEvaluatorData(prev => ({ ...prev, email: e.target.value }))}
                          className={styles.input}
                          placeholder="evaluador@universidad.edu"
                          disabled={invitingExternal}
                        />
                      </label>
                    </div>
                    <div className={styles.formRow}>
                      <label className={styles.label}>
                        Nombre Completo *
                        <input
                          type="text"
                          value={inviteEvaluatorData.name}
                          onChange={(e) => setInviteEvaluatorData(prev => ({ ...prev, name: e.target.value }))}
                          className={styles.input}
                          placeholder="Dr. Juan Pérez"
                          disabled={invitingExternal}
                        />
                      </label>
                    </div>
                    <div className={styles.formActions}>
                      <Button
                        variant="secondary"
                        onClick={() => setShowInviteForm(false)}
                        disabled={invitingExternal}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="primary"
                        onClick={handleInviteEvaluator}
                        disabled={invitingExternal}
                      >
                        {invitingExternal ? 'Invitando...' : 'Invitar y Asignar'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className={styles.footer}>
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </div>
  );
};

export default EvaluatorAssignmentModal;